import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FolderKanban, Loader2, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SearchableFilterSelect } from "@sd/components/common/SearchableFilterSelect";
import { SearchableMultiFilterSelect } from "@sd/components/common/SearchableMultiFilterSelect";
import { DeleteConfirmDialog } from "@sd/components/common/DeleteConfirmDialog";
import ServiceDeskAPIService, {
  ptsOpenProjectListQueryKey,
  serviceDeskExternalUserListQueryKey,
  serviceDeskUserProjectQueryKey,
} from "@sd/services/APIs";
import { ptsDropdownOptionsFromPayload } from "@sd/lib/ptsDropdownOptions";
import { looksLikeOpaqueUserId } from "@sd/lib/ticketRowMapping";

type UnknownRecord = Record<string, unknown>;
type NamedOption = { value: string; label: string; email?: string };
type AssignmentRow = {
  mappingId: string;
  userId: string;
  userLabel: string;
  projectId: string;
  projectLabel: string;
};

function asText(value: unknown, fallback = ""): string {
  if (value === null || value === undefined) return fallback;
  const parsed = String(value).trim();
  return parsed || fallback;
}

function toArrayPayload(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== "object") return [];
  const payload = raw as UnknownRecord;
  for (const key of ["results", "data", "items", "records", "rows", "users"]) {
    const val = payload[key];
    if (Array.isArray(val)) return val;
  }
  return [];
}

function extractId(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (value && typeof value === "object") {
    const row = value as UnknownRecord;
    return asText(row.id ?? row.uuid ?? row.pk ?? row.user_id ?? row.project_id);
  }
  return "";
}

function humanLabel(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object") {
      const nested = humanLabel(
        (candidate as UnknownRecord).full_name,
        (candidate as UnknownRecord).fullName,
        (candidate as UnknownRecord).projectname,
        (candidate as UnknownRecord).project_name,
        (candidate as UnknownRecord).name,
        (candidate as UnknownRecord).label,
        (candidate as UnknownRecord).title,
        (candidate as UnknownRecord).email
      );
      if (nested) return nested;
      continue;
    }
    const text = asText(candidate);
    if (text && text !== "null" && !looksLikeOpaqueUserId(text)) return text;
  }
  return "";
}

function personLabel(row: UnknownRecord, fallbackId = ""): string {
  return (
    humanLabel(
      row.full_name,
      row.fullName,
      [asText(row.first_name ?? row.firstName), asText(row.last_name ?? row.lastName)]
        .filter(Boolean)
        .join(" "),
      row.name,
      row.username,
      row.email
    ) || fallbackId
  );
}

function projectDisplayName(row: UnknownRecord, fallbackId = ""): string {
  return (
    humanLabel(
      row.full_name,
      row.fullName,
      row.projectname,
      row.project_name,
      row.name,
      row.label,
      row.title,
      row.project
    ) || fallbackId
  );
}

function parseExternalUserOptions(payload: unknown): NamedOption[] {
  const rows = toArrayPayload(payload);
  const source =
    rows.length > 0
      ? rows
      : payload && typeof payload === "object" && !Array.isArray(payload)
        ? [payload]
        : [];

  const out: NamedOption[] = [];
  for (const raw of source) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as UnknownRecord;
    const nestedUser =
      row.user && typeof row.user === "object"
        ? (row.user as UnknownRecord)
        : null;
    const sourceRow = nestedUser ?? row;
    const id = extractId(
      sourceRow.id ??
        sourceRow.user_id ??
        sourceRow.uuid ??
        sourceRow.pk ??
        row.user
    );
    if (!id) continue;
    const label = personLabel(sourceRow, id);
    const email = asText(sourceRow.email ?? row.email);
    out.push({ value: id, label, email: email || undefined });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

function unwrapUserProjectRows(payload: unknown): UnknownRecord[] {
  if (!payload) return [];
  if (Array.isArray(payload)) {
    return payload.filter(
      (item): item is UnknownRecord => Boolean(item) && typeof item === "object"
    );
  }
  if (typeof payload !== "object") return [];
  const row = payload as UnknownRecord;
  for (const key of ["results", "data", "items", "records", "rows"]) {
    const nested = row[key];
    if (Array.isArray(nested) && nested.some(item => item && typeof item === "object")) {
      return unwrapUserProjectRows(nested);
    }
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const nestedRow = nested as UnknownRecord;
      if (nestedRow.id || nestedRow.user || nestedRow.projects) {
        return unwrapUserProjectRows(nested);
      }
    }
  }
  return [row];
}

function extractMappingId(row: UnknownRecord, userId: string): string {
  const mappingId = asText(
    row.id ?? row.uuid ?? row.pk ?? row.mapping_id ?? row.user_project_id
  );
  if (!mappingId || mappingId === userId) return "";
  return mappingId;
}

function parseProjectsFromValue(
  value: unknown
): { id: string; label: string }[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.flatMap(item => parseProjectsFromValue(item));
  }
  if (typeof value === "string" || typeof value === "number") {
    const id = String(value).trim();
    return id ? [{ id, label: id }] : [];
  }
  if (typeof value !== "object") return [];
  const row = value as UnknownRecord;
  const nestedProject =
    row.project && typeof row.project === "object"
      ? (row.project as UnknownRecord)
      : null;
  const id =
    asText(
      row.project_id ??
        (typeof row.project === "string" || typeof row.project === "number"
          ? row.project
          : "")
    ) || extractId(nestedProject);
  if (!id) return [];
  const label =
    projectDisplayName(nestedProject ?? {}, "") ||
    projectDisplayName(row, "") ||
    id;
  return [{ id, label }];
}

function parseUserProjectAssignments(payload: unknown): AssignmentRow[] {
  const rows: AssignmentRow[] = [];
  for (const row of unwrapUserProjectRows(payload)) {
    const userRaw = row.user ?? row.user_id ?? row.external_user ?? row.created_by;
    const userId =
      extractId(userRaw) ||
      asText(row.user_id) ||
      (typeof userRaw === "string" ? userRaw.trim() : "");
    const mappingId = extractMappingId(row, userId);
    const userLabel =
      (userRaw && typeof userRaw === "object"
        ? personLabel(userRaw as UnknownRecord, "")
        : "") ||
      humanLabel(
        row.full_name,
        row.fullName,
        row.user_full_name,
        row.user_name,
        row.user_email,
        row.email,
        typeof userRaw === "string" && !looksLikeOpaqueUserId(userRaw)
          ? userRaw
          : ""
      ) ||
      userId;

    let projects = parseProjectsFromValue(
      row.projects ?? row.project_ids ?? row.project ?? row.project_id
    );

    if (projects.length === 0) {
      projects = parseProjectsFromValue(row).filter(
        project => project.id !== mappingId && project.id !== userId
      );
    }

    const rowFullName = projectDisplayName(row, "");
    if (projects.length === 1 && looksLikeOpaqueUserId(projects[0].label) && rowFullName) {
      projects[0] = { ...projects[0], label: rowFullName };
    }

    for (const project of projects) {
      rows.push({
        mappingId,
        userId,
        userLabel: userLabel || "—",
        projectId: project.id,
        projectLabel: looksLikeOpaqueUserId(project.label)
          ? rowFullName || project.label
          : project.label,
      });
    }
  }
  return rows;
}

function uniqueProjectOptions(rows: AssignmentRow[]): NamedOption[] {
  const map = new Map<string, string>();
  for (const row of rows) {
    if (!row.projectId) continue;
    const existing = map.get(row.projectId);
    const nextLabel = row.projectLabel || row.projectId;
    if (
      !existing ||
      looksLikeOpaqueUserId(existing) ||
      existing === row.projectId
    ) {
      map.set(row.projectId, nextLabel);
    }
  }
  return Array.from(map.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

const dropdownTriggerClass =
  "h-9 w-full justify-between border border-input bg-background px-3 text-sm font-normal shadow-sm";

function initialsFromLabel(label: string): string {
  const cleaned = label.replace(/\(.*?\)/g, " ").trim();
  const parts = cleaned.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (cleaned.slice(0, 2) || "?").toUpperCase();
}

function userProjectErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object") {
    const payload = data as {
      error?: { message?: string } | string;
      message?: string;
      detail?: string;
    };
    if (typeof payload.error === "object" && payload.error?.message) {
      return payload.error.message;
    }
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error;
    }
    if (typeof payload.detail === "string" && payload.detail.trim()) {
      return payload.detail;
    }
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message;
    }
  }
  return "Request failed";
}

async function saveOrUpdateUserProjects(
  data: { user: string; projects: string[] },
  mappingId?: string
) {
  if (mappingId) {
    return ServiceDeskAPIService.userProjects.update(mappingId, data);
  }
  return ServiceDeskAPIService.userProjects.save(data);
}

export function AdminExternalUserAccess({ enabled }: { enabled: boolean }) {
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [editingMappingId, setEditingMappingId] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    mappingId: string;
    userId: string;
    userName: string;
  } | null>(null);

  const usersQuery = useQuery({
    queryKey: serviceDeskExternalUserListQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.externalUsers.list();
      return response.data;
    },
    enabled,
    staleTime: 60_000,
  });

  const listingQuery = useQuery({
    queryKey: serviceDeskUserProjectQueryKey(null),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.userProjects.list();
      return response.data;
    },
    enabled,
    staleTime: 15_000,
  });

  const assignedQuery = useQuery({
    queryKey: serviceDeskUserProjectQueryKey(selectedUserId),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.userProjects.list(
        selectedUserId
      );
      return response.data;
    },
    enabled: enabled && Boolean(selectedUserId),
    staleTime: 15_000,
  });

  const allProjectsQuery = useQuery({
    queryKey: ptsOpenProjectListQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.pts.openProjectList();
      return response.data;
    },
    enabled,
    staleTime: 5 * 60_000,
  });

  const userOptions = useMemo(
    () => parseExternalUserOptions(usersQuery.data),
    [usersQuery.data]
  );
  const listingRows = useMemo(
    () => parseUserProjectAssignments(listingQuery.data),
    [listingQuery.data]
  );
  const assignedRows = useMemo(
    () => parseUserProjectAssignments(assignedQuery.data),
    [assignedQuery.data]
  );
  const assignedProjectIds = useMemo(
    () =>
      Array.from(new Set(assignedRows.map(row => row.projectId).filter(Boolean))),
    [assignedRows]
  );
  const projectOptions = useMemo(
    () => ptsDropdownOptionsFromPayload(allProjectsQuery.data),
    [allProjectsQuery.data]
  );

  const mappingIdForSelectedUser = useMemo(() => {
    if (editingMappingId) return editingMappingId;
    return (
      assignedRows.find(row => row.userId === selectedUserId)?.mappingId ||
      listingRows.find(row => row.userId === selectedUserId)?.mappingId ||
      ""
    );
  }, [editingMappingId, assignedRows, listingRows, selectedUserId]);

  useEffect(() => {
    if (!selectedUserId) {
      setSelectedProjectIds([]);
      return;
    }
    if (isEditing) return;
    if (assignedQuery.isFetching) return;
    setSelectedProjectIds(assignedProjectIds);
  }, [selectedUserId, assignedQuery.isFetching, assignedProjectIds, isEditing]);

  const saveMutation = useMutation({
    mutationFn: async () =>
      saveOrUpdateUserProjects(
        {
          user: selectedUserId,
          projects: selectedProjectIds,
        },
        mappingIdForSelectedUser || undefined
      ),
    onSuccess: async () => {
      const userId = selectedUserId;
      setIsEditing(false);
      setEditingMappingId("");
      setSelectedUserId("");
      setSelectedProjectIds([]);
      toast.success("Projects assigned successfully");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: serviceDeskUserProjectQueryKey(userId),
        }),
        queryClient.invalidateQueries({
          queryKey: serviceDeskUserProjectQueryKey(null),
        }),
      ]);
    },
    onError: error => {
      toast.error(userProjectErrorMessage(error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ mappingId }: { mappingId: string; userId: string }) =>
      ServiceDeskAPIService.userProjects.remove(mappingId),
    onSuccess: async (_data, { mappingId, userId }) => {
      toast.success("Project access removed");
      if (editingMappingId === mappingId || selectedUserId === userId) {
        setIsEditing(false);
        setEditingMappingId("");
        setSelectedUserId("");
        setSelectedProjectIds([]);
      }
      setDeleteTarget(null);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: serviceDeskUserProjectQueryKey(userId),
        }),
        queryClient.invalidateQueries({
          queryKey: serviceDeskUserProjectQueryKey(null),
        }),
      ]);
    },
    onError: error => {
      toast.error(userProjectErrorMessage(error));
    },
  });

  const startEditAccess = (group: {
    mappingId: string;
    userId: string;
    userLabel: string;
    projects: { id: string; label: string }[];
  }) => {
    const userId =
      group.userId ||
      userOptions.find(
        option =>
          option.label === group.userLabel || option.email === group.userLabel
      )?.value ||
      "";
    if (!userId) {
      toast.error("Cannot edit: user id missing");
      return;
    }
    if (!group.mappingId) {
      toast.error("Cannot edit: mapping id missing");
      return;
    }
    setIsEditing(true);
    setEditingMappingId(group.mappingId);
    setSelectedUserId(userId);
    setSelectedProjectIds(
      group.projects.map(project => project.id).filter(Boolean)
    );
    toast.message("Update projects, then click Update");
  };

  const resolveUserLabel = (userId: string, fallback: string) => {
    const match = userOptions.find(
      option =>
        option.value === userId ||
        option.value === fallback ||
        (option.email &&
          (option.email === userId || option.email === fallback)) ||
        option.label === fallback
    );
    if (match?.label && !looksLikeOpaqueUserId(match.label)) return match.label;
    if (fallback && !looksLikeOpaqueUserId(fallback) && fallback !== "—") {
      return fallback;
    }
    if (userId && !looksLikeOpaqueUserId(userId)) return userId;
    return match?.label || fallback || userId || "—";
  };

  const resolveProjectLabel = (projectId: string, fallback: string) => {
    const fromOptions = projectOptions.find(option => option.value === projectId)?.label;
    if (fromOptions && !looksLikeOpaqueUserId(fromOptions)) return fromOptions;
    if (fallback && !looksLikeOpaqueUserId(fallback)) return fallback;
    return fromOptions || fallback || projectId;
  };

  const tableRows = selectedUserId ? assignedRows : listingRows;
  const groupedListing = useMemo(() => {
    const groups: {
      mappingId: string;
      userId: string;
      userLabel: string;
      projects: { id: string; label: string }[];
    }[] = [];
    const indexByUser = new Map<string, number>();

    for (const row of tableRows) {
      const key = row.userId || row.userLabel;
      const existing = indexByUser.get(key);
      if (existing === undefined) {
        indexByUser.set(key, groups.length);
        groups.push({
          mappingId: row.mappingId,
          userId: row.userId,
          userLabel: row.userLabel,
          projects: [{ id: row.projectId, label: row.projectLabel }],
        });
        continue;
      }
      const group = groups[existing];
      if (!group.mappingId && row.mappingId) group.mappingId = row.mappingId;
      if (!group.projects.some(project => project.id === row.projectId)) {
        group.projects.push({ id: row.projectId, label: row.projectLabel });
      }
    }
    return groups;
  }, [tableRows]);
  const listingLoading = selectedUserId
    ? assignedQuery.isPending
    : listingQuery.isPending;
  const dropdownsLoading =
    usersQuery.isPending || listingQuery.isPending || allProjectsQuery.isPending;
  const canSave =
    Boolean(selectedUserId) &&
    selectedProjectIds.length > 0 &&
    !saveMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4" />
          External User Access
        </CardTitle>
        <CardDescription>
          Assign visible projects for external users
        </CardDescription>
        {dropdownsLoading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : null}
        {usersQuery.isError ? (
          <p className="text-xs text-destructive">Failed to load external users</p>
        ) : null}
        {listingQuery.isError ? (
          <p className="text-xs text-destructive">Failed to load user project listing</p>
        ) : null}
        {allProjectsQuery.isError ? (
          <p className="text-xs text-destructive">Failed to load projects</p>
        ) : null}
      </CardHeader>
      <CardContent className="min-w-0 space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>External user</Label>
            <SearchableFilterSelect
              value={selectedUserId}
              onValueChange={userId => {
                setIsEditing(false);
                setSelectedUserId(userId);
                const mappingId =
                  listingRows.find(row => row.userId === userId)?.mappingId ||
                  "";
                setEditingMappingId(mappingId);
              }}
              options={userOptions}
              placeholder="Select external user"
              searchPlaceholder="Search users…"
              emptyMessage="No external users found"
              disabled={usersQuery.isPending}
              triggerClassName={dropdownTriggerClass}
            />
            <p className="text-xs text-muted-foreground">
              Choose who should get project access
            </p>
          </div>
          <div className="space-y-2">
            <Label>Projects</Label>
            <SearchableMultiFilterSelect
              values={selectedProjectIds}
              onValuesChange={setSelectedProjectIds}
              options={projectOptions}
              placeholder="Select projects"
              searchPlaceholder="Search projects…"
              emptyMessage="No projects found"
              disabled={allProjectsQuery.isPending}
              triggerClassName={dropdownTriggerClass}
            />
            <p className="text-xs text-muted-foreground">
              One user can have multiple projects
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            disabled={!canSave}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {mappingIdForSelectedUser ? "Updating" : "Saving"}
              </>
            ) : mappingIdForSelectedUser ? (
              "Update"
            ) : (
              "Save"
            )}
          </Button>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-foreground">
              Assigned access
            </h2>
            {groupedListing.length > 0 ? (
              <span className="text-xs text-muted-foreground">
                {groupedListing.length}{" "}
                {groupedListing.length === 1 ? "user" : "users"}
              </span>
            ) : null}
          </div>

          {listingLoading ? (
            <div className="rounded-xl border bg-muted/30 px-4 py-10 text-center text-sm text-muted-foreground">
              Loading assigned projects…
            </div>
          ) : groupedListing.length === 0 ? (
            <div className="rounded-xl border border-dashed bg-muted/20 px-4 py-10 text-center">
              <Users className="mx-auto mb-2 h-8 w-8 text-muted-foreground/70" />
              <p className="text-sm font-medium text-foreground">
                No access assigned yet
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Select a user and projects, then click Save
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {groupedListing.map(group => {
                const userName = resolveUserLabel(group.userId, group.userLabel);
                const projectCount = group.projects.length;
                return (
                  <div
                    key={group.userId || group.userLabel}
                    className="rounded-xl border bg-card p-4 shadow-sm"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                        {initialsFromLabel(userName)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-semibold text-foreground">
                            {userName}
                          </p>
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                            {projectCount}{" "}
                            {projectCount === 1 ? "project" : "projects"}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {group.projects.map(project => (
                            <Badge
                              key={project.id}
                              variant="secondary"
                              className="gap-1.5 rounded-full px-2.5 py-1 font-medium"
                            >
                              <FolderKanban className="h-3.5 w-3.5" />
                              {resolveProjectLabel(project.id, project.label)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label={`Edit access for ${userName}`}
                          onClick={() => startEditAccess(group)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          aria-label={`Delete access for ${userName}`}
                          disabled={deleteMutation.isPending}
                          onClick={() => {
                            if (!group.mappingId) {
                              toast.error("Cannot delete: mapping id missing");
                              return;
                            }
                            const userId =
                              group.userId ||
                              userOptions.find(
                                option =>
                                  option.label === userName ||
                                  option.email === userName
                              )?.value ||
                              "";
                            setDeleteTarget({
                              mappingId: group.mappingId,
                              userId,
                              userName,
                            });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
      <DeleteConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={open => {
          if (!open && !deleteMutation.isPending) setDeleteTarget(null);
        }}
        title="Remove project access?"
        description="This user will no longer have access to the assigned projects."
        itemCaption="User"
        itemLabel={deleteTarget?.userName ?? ""}
        warningMessage="You can assign projects again later from this tab."
        confirmButtonLabel="Remove access"
        confirming={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget?.mappingId) {
            deleteMutation.mutate({
              mappingId: deleteTarget.mappingId,
              userId: deleteTarget.userId,
            });
          }
        }}
      />
    </Card>
  );
}
